import { fetchAuthSession } from "aws-amplify/auth";
import {
	ClientError,
	GraphQLClient,
	RequestOptions,
	Variables,
} from "graphql-request";

// Private client uses Cognito JWT for authenticated requests
const privateClient = new GraphQLClient(process.env.APPSYNC_API_URL!);

// Public client routes through the proxy which adds the API key server-side
const publicClient = new GraphQLClient(`${process.env.REST_API_URL}/graphql`);

export interface GqlReqOptions<TVariables extends Variables = Variables> {
	query: string;
	variables?: TVariables;
	headers?: Record<string, string>;
	signal?: AbortSignal;
	useKey?: boolean;
}

export const gqlReq = async <
	TQuery extends Record<string, any>,
	TVariables extends Variables = Variables,
>({
	query,
	variables,
	headers,
	useKey = false,
	signal,
}: GqlReqOptions<TVariables>): Promise<TQuery[keyof TQuery]> => {
	const requestHeaders: RequestOptions["requestHeaders"] = {
		...headers,
	};

	// useKey: true  -> publicClient (proxy adds x-api-key header)
	// useKey: false -> privateClient with Cognito JWT
	const client = useKey ? publicClient : privateClient;

	if (!useKey) {
		const session = await fetchAuthSession();
		const token = session.tokens?.idToken?.toString();
		if (!token) throw new Error("Failed to get token");
		// eslint-disable-next-line @typescript-eslint/dot-notation
		requestHeaders["Authorization"] = `Bearer ${token}`;
	}

	return client
		.request<TQuery>({
			document: query,
			variables,
			requestHeaders,
			signal,
		})
		.then((res) => res[Object.keys(res)[0]!])
		.catch((e: ClientError) => {
			const errorMessage =
				e.response.errors?.[0]?.message ?? "Something went wrong!";
			throw new Error(errorMessage);
		});
};
